import { createNamespacedHelpers } from 'vuex';
import { TweenMax, Back } from 'gsap';

const typingActivity = createNamespacedHelpers('activity/typing');
const keyboard = createNamespacedHelpers('keyboard');
const application = createNamespacedHelpers('application');
const activity = createNamespacedHelpers('activity');

export default {
  props: {
    ipadExternalTab: Boolean,
    theme: {
      type: String,
      default: 'light'
    }
  },

  data() {
    return {
      activeKeys: {},
      isShowing: false,
      currentShiftKey: '',
      isVisible: false
    };
  },

  computed: {
    ...activity.mapState({
      activityName: state => state.data.type
    }),
    ...typingActivity.mapState(['attemptedKey']),
    ...keyboard.mapState({
      isGhosting: state => state.settings.isGhosting,
      hideNumberRow: state => state.settings.hideNumberRow,
      showOutline: state => state.settings.showOutline,
      showTargets: state => state.settings.showTargets,
      shouldShow: state => state.show,
      keyboardCharacters: state => state.settings.keys,
      keyboardDisabled: state => !state.enabled,
      keyboardType: state => state.settings.keyboardType,
      roofs: state => state.settings.roofs,
      showKeyboardAtStart: state => state.settings.showKeyboardAtStart
    }),
    ...keyboard.mapState(['holdKeyPressed', 'capsLock']),
    ...application.mapState(['device']),
    ...application.mapGetters(['ipadWithoutExternalKeyboard', 'usingExternalKeyboard']),
    ...keyboard.mapGetters(['activeLetters']),
    ...typingActivity.mapGetters(['targetKey', 'expectedKey']),
    ...typingActivity.mapGetters(['isTouchHold']),

    lockForCapitals() {
      return (
        this.expectedKey &&
        this.expectedKey !== ' ' &&
        this.expectedKey === this.expectedKey.toUpperCase() &&
        !this.holdKeyPressed
      );
    },

    keyboardLocked() {
      return this.targetKey.includes('shift')
        ? this.lockForCapitals
        : this.isTouchHold && !this.holdKeyPressed;
    },

    restrictedLetters() {
      const activeLetters = this.activeLetters || '';
      return activeLetters.toLowerCase();
    }
  },

  mounted() {
    // the listeners are defined in each keyboard component
    this.addKeyListeners();
  },

  beforeDestroy() {
    // remove listeners before destroying component.
    // ensures we don't run into errors where two functions respond to the same event
    this.removeKeyListeners();
  },

  methods: {
    ...typingActivity.mapActions(['attemptKey']),
    ...typingActivity.mapMutations(['resetAttemptedKey']),
    ...keyboard.mapMutations([
      'enableKeyboard',
      'disableKeyboard',
      'pressHoldKey',
      'releaseHoldKey',
      'setCapsLock'
    ]),

    // stump functions, should be declared in keyboard components
    addKeyListeners() {},
    removeKeyListeners() {},

    keyNotUsedInActivity(key, event) {
      // TODO: We need to map keyCodes to characters to handle Edge :(
      if (key === 'Unidentified') return false;

      // Transform Shift Left/Right to Shift1/Shift2 keys
      const otherShiftWasPressed =
        event &&
        event.shiftKey &&
        event.code.includes('Shift') &&
        this.currentShiftKey !== event.code;

      if (key === 'Shift' && event && !this.currentShiftKey) {
        this.currentShiftKey = event.code;
      } else if (otherShiftWasPressed) {
        this.currentShiftKey = event.code;
        if (this.currentShiftKey === 'ShiftLeft') key = 'Shift2';
        if (this.currentShiftKey === 'ShiftRight') key = 'Shift1';
      } else if (event && event.shiftKey && this.currentShiftKey) {
        if (this.currentShiftKey === 'ShiftLeft') key = 'Shift1';
        if (this.currentShiftKey === 'ShiftRight') key = 'Shift2';
      } else {
        this.currentShiftKey = '';
      }

      if (this.targetKey.toLowerCase() === key.toLowerCase()) return false;

      return this.restrictedLetters && !this.restrictedLetters.includes(key.toLowerCase());
    },

    handleKeyboardAppeared() {
      this.isVisible = true;
    },

    handleKeyboardRemoved() {
      this.isVisible = false;
    },

    getKeyCode({ key, event }) {
      if (key) return key.toLowerCase();

      // We check for the keycode here because for punctuation, Edge registers event.key
      // on keyup events as 'Unidentified'.
      return event.which || event.keyCode || 0;
    },

    addToActiveKeys(keyOptions) {
      this.activeKeys[this.getKeyCode(keyOptions)] = true;
    },

    removeFromActiveKeys(keyOptions) {
      this.activeKeys[this.getKeyCode(keyOptions)] = false;
    },

    clearActiveKeys() {
      this.activeKeys = {};
    },

    keyIsUp(keyOptions) {
      return !this.activeKeys[this.getKeyCode(keyOptions)];
    },

    keyIsPressed(keyOptions) {
      return !!this.activeKeys[this.getKeyCode(keyOptions)];
    },

    holdKeyIsPressed(key) {
      return this.isTouchHold && key === this.targetKey;
    },

    handleKeyDown(event) {
      const location = event.location; // eslint-disable-line prefer-destructuring

      if (event.preventDefault) {
        event.preventDefault();
      }

      // Handler can be called with an event from window listener, or key name from touch event.
      let key = event.key || event;

      if (this.doNotHandleKey(event, key)) return;

      if (key === 'special') {
        this.showSpecialKeys = !this.showSpecialKeys;
        return;
      }
      let isCapsLock = false;
      if (this.checkIpadCapsLock(event)) {
        isCapsLock = !isCapsLock;
        this.colorizeCapsLock();
        if (this.ipadWithoutExternalKeyboard) return;
      } else if (this.externalKeyboard) {
        isCapsLock = false;
      }

      if (isCapsLock) {
        key = key.toUpperCase();
      }

      if (this.timerActive) {
        this.resetTimer();
      }

      if (this.isShiftKey(key)) {
        this.shiftKeyDown = true;
        key += location || '';
      } else if (this.ipadWithoutExternalKeyboard && this.shiftKeyDown) {
        key = this.modifyWithShift(key);
      }

      if (event.getModifierState) {
        if (event.getModifierState('CapsLock')) {
          isCapsLock = true;
        } else {
          isCapsLock = false;
        }
      }

      if (isCapsLock !== this.capsLock) {
        this.setCapsLock(isCapsLock);
      }

      if (key !== 'CapsLock') {
        this.keyPressed(key);
      }
    },

    keyPressed(letter) {
      // TODO MAYBE: don't send keys unless it's available to send
      let letterPressed = this.normalizeCharacterToMatchCMS(letter);
      if (this.isShiftKey(letterPressed)) {
        this.colorizeIt(letterPressed.toLowerCase(), 'highlight');
        letterPressed = 'Shift';
        this.shiftedTouch = true;
      }

      if (this.restricted && !this.activeKeys.includes(letterPressed)) {
        // Don't add shift to activeKey array. shift is a 'free' key
        if (!this.isShiftKey(letterPressed)) {
          this.activeKeys[letterPressed] = true;
        }
        // iOS doesn't register modifier key events by themseleves.
        // Thus, shift doesn't register an event when it's pressed. Send the
        // keyPressed event anyway in this case and check if it's capitalized in the activity.
        const isExternalKeyShiftTarget = this.usingExternalKeyboard && this.targetKey === 'Shift';

        if (letterPressed === this.targetKey) {
          this.$dispatch('targetKeyPressed', this.targetKey);
          this.enableKeyboard();
        } else if (!this.restrictionActive || isExternalKeyShiftTarget) {
          this.$dispatch('keyPressed', letterPressed);
          this.enableKeyboard();
        }
        // Do nothing if shift is pressed.
      } else if (!this.isShiftKey(letterPressed)) {
        this.attemptKey(letterPressed);
      }
    },

    handleKeyUp(event) {
      const key = event.key || event;

      if (this.restrictionActive) return;

      this.activeKeys[key] = null;

      if (this.isShiftKey(key)) {
        this.shiftKeyDown = false;
      }

      this.resetAttemptedKey();
      if (this.tabTimeout) this.clearTabTimeout();

      // Disable keyboard for touch and hold if target isn't space (' ') and
      // student is releasing the target or shift key
      const targetHoldKey = this.targetKey.toLowerCase();
      if (targetHoldKey && (key === targetHoldKey || key.includes(targetHoldKey))) {
        this.disableKeyboard();
      }
    },

    /* iPad w/ external keyboard needs the iPad hold key to be pressed.
       If the keyboard is locked and we get a key event, do nothing. */
    doNotHandleKey(event, key) {
      const isUsingExternalKeyboardOnIpad =
        this.usingExternalKeyboard &&
        this.isTouchAndHold &&
        this.restrictionActive &&
        typeof event !== 'string';

      return (
        key !== 'Backspace' &&
        (this.keyboardDisabled || this.activeKeys.get(key) || isUsingExternalKeyboardOnIpad)
      );
    },

    checkIpadCapsLock(event) {
      const key = event.key || event;
      const character = key.toString();
      const isUsingExternalKeyboardAndCapitalized =
        this.usingExternalKeyboard &&
        !event.shiftKey &&
        character.toUpperCase() === character &&
        character.toLowerCase() !== character;

      return (
        (this.ipadWithoutExternalKeyboard && key === 'CapLock') ||
        isUsingExternalKeyboardAndCapitalized
      );
    },

    isShiftKey(keyPressed) {
      const key = keyPressed.toLowerCase();
      // iOS registers Shift as 'Dead'
      return key.includes('shift') || key === 'dead';
    },

    normalizeCharacterToMatchCMS(key) {
      const normalized = {
        enter: 'enter',
        spacebar: ' ',
        quotationmark: '"',
        tab: 'tab'
      };

      return normalized[key.toLowerCase()] || key;
    },

    enter() {
      const self = this;
      const el = document.getElementById('phoenix-keyboard-wrapper');
      if (!el) return;

      TweenMax.to(el, 1, {
        y: 0,
        ease: Back.easeOut.config(1.2),
        onComplete() {
          self.$root.$emit('keyboard-visibility-change');
        }
      });
    },

    leave() {
      const self = this;
      const el = document.getElementById('phoenix-keyboard-wrapper');
      if (!el) return;

      TweenMax.to(el, 0.65, {
        transform: 'translateY(150vh)',
        ease: Back.easeIn.config(1.7),
        onComplete() {
          self.$root.$emit('keyboard-visibility-change');
        }
      });
    }
  },

  watch: {
    shouldShow(newValue, oldValue) {
      if (newValue === oldValue) return;
      if (newValue) {
        this.enter();
      } else {
        this.leave();
      }
    }
  }
};
